import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, Toggle } from "../../ui/components";
import { dateTime } from "../../ui/format";

interface StaffUser {
  id: string;
  name: string;
  login: string;
  role: "owner" | "shift_lead" | "kitchen" | "courier";
  active: boolean;
  lastLoginAt: string | null;
  activeSessions: number;
  createdAt: string;
}

const ROLES: { id: StaffUser["role"]; title: string; hint: string }[] = [
  { id: "owner", title: "Владелец", hint: "Полный доступ, включая учётные записи" },
  { id: "shift_lead", title: "Старший смены", hint: "Заказы, отмены, возвраты, стоп-лист" },
  { id: "kitchen", title: "Кухня", hint: "Приготовление, заметки, печать, правка состава" },
  { id: "courier", title: "Доставщик", hint: "Только свои доставки" }
];

function roleTitle(role: string): string {
  return ROLES.find(item => item.id === role)?.title ?? role;
}

export function StaffPage() {
  const { staff: me } = useSession();
  const { run, notify } = useToasts();

  const [list, setList] = useState<StaffUser[] | null>(null);
  const [editing, setEditing] = useState<StaffUser | null | undefined>(undefined);
  const [passwordFor, setPasswordFor] = useState<StaffUser | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<StaffUser[]>("/api/admin/staff"));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить сотрудников", "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setActive(user: StaffUser, active: boolean) {
    const ok = await run(
      () => api.patch(`/api/admin/staff/${user.id}`, { active }),
      active ? `${user.name} снова может входить` : `${user.name} заблокирован`
    );
    if (ok) void load();
  }

  async function revokeSessions(user: StaffUser) {
    if (!confirm(`Завершить все сессии сотрудника ${user.name}? Он выйдет из системы на всех устройствах.`)) return;

    const ok = await run(
      () => api.post(`/api/admin/staff/${user.id}/sessions/revoke`),
      `Сессии ${user.name} завершены`
    );
    if (ok) void load();
  }

  if (!list) return <Empty>Загружаем сотрудников…</Empty>;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Доступ</p>
          <h1>Сотрудники</h1>
        </div>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing(null)}>
          + Сотрудник
        </button>
      </div>

      <section className="panel">
        <header>
          <div>
            <h2>Учётные записи</h2>
            <p>Можно завести общую запись на смену или отдельную на каждого — как вам удобнее.</p>
          </div>
        </header>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Роль</th>
                <th>Последний вход</th>
                <th>Сессии</th>
                <th>Доступ</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map(user => {
                const isMe = user.id === me?.id;

                return (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                      {isMe ? <span className="chip"> это вы</span> : null}
                      <br />
                      <small className="muted num">{user.login}</small>
                    </td>
                    <td>{roleTitle(user.role)}</td>
                    <td className="num tiny">{user.lastLoginAt ? dateTime(user.lastLoginAt) : "ни разу"}</td>
                    <td className="num">
                      {user.activeSessions > 0 ? (
                        <span className="chip chip-ready">{user.activeSessions}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <label className="switch-label">
                        <Toggle
                          checked={user.active}
                          disabled={isMe}
                          label={`Доступ для ${user.name}`}
                          onChange={next => void setActive(user, next)}
                        />
                        <span className="tiny muted">{user.active ? "активен" : "заблокирован"}</span>
                      </label>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-sm" onClick={() => setEditing(user)}>
                          Изменить
                        </button>
                        <button type="button" className="btn btn-sm" onClick={() => setPasswordFor(user)}>
                          Пароль
                        </button>
                        {user.activeSessions > 0 ? (
                          <button type="button" className="btn btn-sm" onClick={() => void revokeSessions(user)}>
                            Выйти везде
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="tiny muted">
          Себе снять доступ нельзя — иначе вернуться в систему можно будет только через базу данных. Последнего
          владельца тоже нельзя заблокировать или понизить: сначала назначьте другого.
        </p>
      </section>

      {editing !== undefined ? (
        <StaffForm
          user={editing}
          onClose={() => {
            setEditing(undefined);
            void load();
          }}
        />
      ) : null}

      {passwordFor ? (
        <PasswordForm
          user={passwordFor}
          onClose={() => {
            setPasswordFor(null);
            void load();
          }}
        />
      ) : null}
    </>
  );
}

function StaffForm({ user, onClose }: { user: StaffUser | null; onClose: () => void }) {
  const { run } = useToasts();
  const [name, setName] = useState(user?.name ?? "");
  const [login, setLogin] = useState(user?.login ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<StaffUser["role"]>(user?.role ?? "kitchen");

  const ready = user ? name.trim().length > 0 : name.trim() && login.trim() && password.length >= 8;

  async function save() {
    const ok = await run(
      () =>
        user
          ? api.patch(`/api/admin/staff/${user.id}`, { name: name.trim(), role })
          : api.post("/api/admin/staff", { name: name.trim(), login: login.trim(), password, role }),
      user ? "Сотрудник сохранён" : "Сотрудник создан"
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={user ? `Сотрудник ${user.name}` : "Новый сотрудник"}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!ready}>
            Сохранить
          </button>
        </>
      }
    >
      <label className="field">
        <span>Имя</span>
        <input autoFocus value={name} onChange={event => setName(event.target.value)} placeholder="Иван Петров" />
      </label>

      {user ? (
        <p className="tiny muted">
          Логин <b>{user.login}</b> изменить нельзя — он связан с записями в журнале действий.
        </p>
      ) : (
        <>
          <label className="field">
            <span>Логин</span>
            <input
              value={login}
              onChange={event => setLogin(event.target.value)}
              placeholder="ivan"
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Пароль</span>
            <input
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="new-password"
              placeholder="не короче восьми символов"
            />
          </label>
        </>
      )}

      <div className="field">
        <span>Роль</span>
        {ROLES.map(item => (
          <label className="opt-role" key={item.id}>
            <input
              type="radio"
              name="role"
              checked={role === item.id}
              onChange={() => setRole(item.id)}
            />
            <span>
              <b>{item.title}</b>
              <small className="muted tiny">{item.hint}</small>
            </span>
          </label>
        ))}
      </div>
    </Modal>
  );
}

function PasswordForm({ user, onClose }: { user: StaffUser; onClose: () => void }) {
  const { run } = useToasts();
  const [password, setPassword] = useState("");

  async function save() {
    const ok = await run(
      () => api.post(`/api/admin/staff/${user.id}/password`, { password }),
      `Пароль изменён, ${user.name} вышел из системы`
    );
    if (ok) onClose();
  }

  return (
    <Modal
      title={`Новый пароль для ${user.name}`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={password.length < 8}
          >
            Сменить пароль
          </button>
        </>
      }
    >
      <label className="field">
        <span>Пароль</span>
        <input
          autoFocus
          type="password"
          value={password}
          onChange={event => setPassword(event.target.value)}
          autoComplete="new-password"
          placeholder="не короче восьми символов"
        />
      </label>
      <p className="tiny muted">
        Все открытые сессии сотрудника завершатся: если пароль увели, старая вкладка перестанет работать сразу.
        Передайте новый пароль лично.
      </p>
    </Modal>
  );
}
